import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CountUp from 'react-countup';
import { getClientsRapport } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeActionButton,
  NdoeResponsiveTable, NdoeDataCard, NdoeStatusBadge, NdoeError, NdoeLoading,
} from '../../components/ndoe';
import { toCsv, downloadCsv } from './exportCsv';
import { money, dateTxt } from './format';
import './rapports.css';

// Étiquettes de segment : qui est cette cliente, en un coup d'œil.
const TAGS = {
  vip: { tone: 'gold', label: 'VIP' },
  fidele: { tone: 'ok', label: 'Fidèle' },
  nouvelle: { tone: 'info', label: 'Nouvelle' },
};
const tagBadges = (tags = []) => tags.map((t) => (
  TAGS[t] ? <NdoeStatusBadge key={t} kind="custom" tone={TAGS[t].tone} label={TAGS[t].label} /> : null
));

export default function RapportClients() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const [period, setPeriod] = useState({ period: 30 });
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
  }), [period]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getClientsRapport(apiParams);
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger le rapport clientes.');
    } finally { setLoading(false); }
  }, [apiParams]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const handleExport = () => {
    if (!payload) return;
    setExporting(true);
    try {
      const columns = [
        { title: 'Cliente', value: (r) => r.nom },
        { title: 'Segments', value: (r) => (r.tags || []).join(' ') },
        { title: 'CA période ($)', value: (r) => r.ca },
        { title: 'Commandes période', value: (r) => r.commandes },
        { title: 'Panier moyen ($)', value: (r) => r.panier_moyen },
        { title: 'CA historique ($)', value: (r) => r.ca_total },
        { title: 'Commandes historique', value: (r) => r.commandes_total },
        { title: 'Dernière vente', value: (r) => r.derniere_vente ?? '' },
        { title: 'Récence (jours)', value: (r) => r.recence_jours ?? '' },
      ];
      downloadCsv(`clientes_${payload.meta.start_date}_${payload.meta.end_date}.csv`, toCsv(columns, payload.top_clientes));
      toast.success('Rapport exporté.');
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  const kpis = payload?.kpis || {};
  const meta = payload?.meta || {};
  const highlights = payload?.highlights || {};

  const columns = [
    {
      title: 'Cliente', dataIndex: 'nom',
      render: (nom, r) => (
        <div className="pp-produit">
          <b>{nom}</b>
          <div className="pp-detail__badges">{tagBadges(r.tags)}</div>
        </div>
      ),
    },
    { title: 'CA (période)', dataIndex: 'ca', align: 'right', width: 116, render: (v) => <b className="num">{money(v)}</b> },
    { title: 'Commandes', dataIndex: 'commandes', align: 'right', width: 100, render: (v) => <span className="num">{v}</span> },
    { title: 'Panier moyen', dataIndex: 'panier_moyen', align: 'right', width: 112, render: (v) => <span className="num">{money(v)}</span> },
    { title: 'CA historique', dataIndex: 'ca_total', align: 'right', width: 116, render: (v) => <span className="num">{money(v)}</span> },
    { title: 'Dernière vente', dataIndex: 'derniere_vente', width: 128, render: (v, r) => (
      <div className="rp-rupture"><b>{dateTxt(v)}</b><small>{r.recence_jours !== null ? `il y a ${r.recence_jours} j` : '—'}</small></div>
    ) },
  ];

  const renderCard = (r) => (
    <NdoeDataCard
      key={r.id_client}
      title={r.nom}
      subtitle={`Dernière vente : ${dateTxt(r.derniere_vente)}${r.recence_jours !== null ? ` (il y a ${r.recence_jours} j)` : ''}`}
      badge={tagBadges(r.tags)[0] || null}
      metrics={[
        { label: 'CA période', value: money(r.ca) },
        { label: 'Commandes', value: r.commandes },
        { label: 'Panier', value: money(r.panier_moyen) },
        { label: 'Historique', value: money(r.ca_total) },
      ]}
    />
  );

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Clientes"
        subtitle="Qui achète, qui revient, qui s'éloigne — et qui relancer en premier."
        actions={
          <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Exporter CSV
          </NdoeActionButton>
        }
      />

      <div className="pp-period">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        {meta.start_date && <span className="pp-range">du {dateTxt(meta.start_date)} au {dateTxt(meta.end_date)}</span>}
      </div>

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : loading || !payload ? (
        <NdoeLoading rows={6} />
      ) : (
        <>
          <div className="rv-kpis">
            <div className="rv-kpi rv-kpi--gold">
              <small>Chiffre d'affaires</small>
              <b className="num"><CountUp end={kpis.chiffre_affaires} duration={0.9} separator=" " /> $</b>
            </div>
            <div className="rv-kpi"><small>Clientes actives</small><b className="num"><CountUp end={kpis.clientes_actives} duration={0.9} /></b></div>
            <div className="rv-kpi"><small>Nouvelles</small><b className="num"><CountUp end={kpis.nouvelles} duration={0.9} /></b></div>
            <div className="rv-kpi"><small>Panier moyen</small><b className="num">{money(kpis.panier_moyen)}</b></div>
            <div className="rv-kpi"><small>VIP (moitié du CA)</small><b className="num">{kpis.vip}</b></div>
            <div className="rv-kpi"><small>À relancer</small><b className="num">{kpis.a_relancer}</b></div>
          </div>

          <div className="rv-highlights">
            {highlights.meilleure_cliente && (
              <div className="rv-hl">
                <NdoeStatusBadge kind="custom" tone="gold" label="Meilleure cliente" />
                <b>{highlights.meilleure_cliente.nom}</b>
                <small className="num">{money(highlights.meilleure_cliente.ca)} · {highlights.meilleure_cliente.commandes} commande(s)</small>
              </div>
            )}
            {highlights.concentration?.message && (
              <div className="rv-hl">
                <NdoeStatusBadge kind="custom" tone="info" label="Concentration" />
                <b>Top 5 = {kpis.top5_part_pct} % du CA</b>
                <small>{highlights.concentration.message}</small>
              </div>
            )}
          </div>

          {(payload.a_relancer || []).length > 0 && (
            <section className="ndoe-card rc-relance">
              <div className="ndoe-section__title">À relancer — elles achetaient, elles se sont tues</div>
              <p className="rc-relance__aide">
                Au moins 2 commandes, silencieuses depuis 1 à 6 mois. Triées par valeur : commencez en haut.
              </p>
              <div className="rc-relance__liste">
                {payload.a_relancer.map((c) => (
                  <div className="rc-relance__item" key={c.id_client}>
                    <div className="rc-relance__qui">
                      <b>{c.nom}</b>
                      <small>{c.commandes_total} commande(s) · panier {money(c.panier_moyen_historique)}</small>
                    </div>
                    <div className="rc-relance__quand">
                      <NdoeStatusBadge kind="custom" tone="warn" label={`silencieuse ${c.recence_jours} j`} />
                      <small>dernière vente {dateTxt(c.derniere_vente)}</small>
                    </div>
                    <b className="num rc-relance__ca">{money(c.ca_total)}</b>
                  </div>
                ))}
              </div>
            </section>
          )}

          <h3 className="ve-titre">Top clientes de la période</h3>
          <NdoeResponsiveTable
            className="pp-table"
            data={payload.top_clientes}
            columns={columns}
            renderCard={renderCard}
            rowKey="id_client"
            loading={false}
            scroll={{ x: 900 }}
            pagination={{ pageSize: 15 }}
            empty={{ icon: '👜', title: 'Aucune vente sur la période', text: 'Élargissez la période.' }}
          />
        </>
      )}
    </NdoePage>
  );
}
